import React, { useState, useEffect } from 'react';
import { updateUser, getAllRoles } from '../../services/userService';
import { getAllGroups } from '../../services/groupService';
import { SuccessCircle, WarningIcon } from '../../assets/icons';
import Spinner from '../common/Spinner';
import RoleBadge from '../common/RoleBadge';
import useModalClose from '../../hooks/useModalClose';
import useIlceMahalleSecimi from '../../hooks/useIlceMahalleSecimi';
import IlceMahalleSecimi from './IlceMahalleSecimi';

const STEPS = {
  FORM: 0,
  CONFIRM: 1,
  COMPLETE: 2
};

// Role id for Denetleyici - check by id since name can change
const DENETLEYICI_ROLE_ID = 6;

const normalizeText = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  return text === 'null' || text === 'undefined' ? '' : text;
};

const EditUserModal = ({ isOpen, onClose, user, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isRolesLoading, setIsRolesLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    tcnumber: '',
    email: '',
    grup: '',
    phoneNumber: '05',
    role: '1'
  });
  const [updatedUser, setUpdatedUser] = useState(null);

  const isDenetleyici = formData.role === String(DENETLEYICI_ROLE_ID);
  // Show ilce/mahalle if Denetleyici OR user has ilce/mahlist (legacy or non-Denetleyici with location data)
  const showIlceMahalle = isDenetleyici || !!(user?.ilce || user?.mahlist);
  const ilceMahalle = useIlceMahalleSecimi({
    aktif: showIlceMahalle,
    kayitliIlce: user?.ilce,
    kayitliMahlist: user?.mahlist
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        surname: user.surname,
        tcnumber: user.tcnumber,
        // Eski kayıtların bir kısmında e-posta, bozuk yazım yüzünden "null" METNİ olarak duruyor;
        // kutuda "null" göstermek yerine boş bırakıyoruz.
        email: normalizeText(user.email),
        grup: user.grup,
        phoneNumber: user.phoneNumber || '05',
        role: String(user.role ?? user.roleId ?? '1')
      });
    }
  }, [user]);

  const resetState = () => {
    setCurrentStep(STEPS.FORM);
    setIsLoading(false);
    setError(null);
    setUpdatedUser(null);
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const handleClose = () => {
    resetState();
    onClose();
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [groupsData, rolesData] = await Promise.all([
          getAllGroups(),
          getAllRoles()
        ]);
        setGroups(groupsData || []);
        setRoles(rolesData || []);
      } catch (err) {
        console.error('Failed to fetch groups or roles:', err);
      } finally {
        setIsRolesLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleGroupChange = (userId, newGroupName) => {
    setFormData((prev) => ({
      ...prev,
      grup: newGroupName,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phoneNumber') {
      // If the value is empty or less than 2 characters, reset to '05'
      if (value.length < 2) {
        setFormData(prev => ({
          ...prev,
          [name]: '05'
        }));
        return;
      }

      // Only allow numbers
      const numbersOnly = value.replace(/\D/g, '');
      
      // If the number doesn't start with '05', force it
      if (!numbersOnly.startsWith('05')) {
        const correctedNumber = '05' + numbersOnly.slice(2);
        setFormData(prev => ({
          ...prev,
          [name]: correctedNumber.slice(0, 11)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: numbersOnly.slice(0, 11)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    if (!formData.name || !formData.surname || !formData.tcnumber || !formData.phoneNumber || !formData.role) {
      setError('Lütfen tüm alanları doldurunuz.');
      return false;
    }
    if (isDenetleyici) {
      const ilceMahalleHatasi = ilceMahalle.dogrula();
      if (ilceMahalleHatasi) {
        setError(ilceMahalleHatasi);
        return false;
      }
    }
    if (formData.tcnumber.length !== 11 || !/^\d+$/.test(formData.tcnumber)) {
      setError('Geçerli bir TC Kimlik Numarası giriniz.');
      return false;
    }
    // Check if TC number ends with an even digit
    const lastDigit = parseInt(formData.tcnumber.slice(-1));
    if (lastDigit % 2 !== 0) {
      setError('TC Kimlik Numarası çift rakam ile bitmelidir.');
      return false;
    }

    // Phone validation
    if (formData.phoneNumber.length !== 11 || !formData.phoneNumber.startsWith('05')) {
      setError('Telefon numarası 05 ile başlamalı ve 11 haneli olmalıdır.');
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    
    if (validateForm()) {
      setCurrentStep(STEPS.CONFIRM);
    }
  };

  const handleBack = () => {
    setCurrentStep(STEPS.FORM);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);
    setCurrentStep(STEPS.COMPLETE);

    try {
      // Users.Ilce/Mahlist'e ADLAR virgülle yazılır (seçimdeki id'ler değil); dokunulmamış ve
      // çözülemeyen kayıt olduğu gibi geri yazılır (bkz. useIlceMahalleSecimi).
      // Boş değer gönderilmez, backend alanı boşaltır.
      const { ilce, mahlist } = showIlceMahalle
        ? ilceMahalle.kayitDegerleri()
        : { ilce: '', mahlist: '' };
      const payload = { ...formData, ilce, mahlist, isActive: true };
      await updateUser(user.id, payload);
      const updatedUserData = {
        ...user,
        ...formData,
        ilce,
        mahlist,
        role: parseInt(formData.role)
      };
      setUpdatedUser(updatedUserData);
      onSuccess(updatedUserData);
    } catch (error) {
      // Backend kapsam/doğrulama hatalarını açıklayıcı mesajla döndürüyor (ör. e-posta öneki);
      // genel metinle örtersek kullanıcı neyi düzelteceğini bilemiyor.
      setError(error?.response?.data?.message || 'Kullanıcı güncellenirken bir hata oluştu.');
      console.error('Update user error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="stepper">
      <div className={`step ${currentStep >= STEPS.FORM ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.FORM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Kişi Bilgileri</span>
      </div>
      <div className={`step ${currentStep >= STEPS.CONFIRM ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.CONFIRM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Bilgi Onayı</span>
      </div>
      <div className={`step ${currentStep >= STEPS.COMPLETE ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.COMPLETE && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Tamamlandı</span>
      </div>
    </div>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="create-user-form">
      <div className="form-group">
        <label htmlFor="name">Ad</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Ad"
        />
      </div>
      <div className="form-group">
        <label htmlFor="surname">Soyad</label>
        <input
          type="text"
          id="surname"
          name="surname"
          value={formData.surname}
          onChange={handleInputChange}
          placeholder="Soyad"
        />
      </div>
      <div className="form-group">
        <label htmlFor="tcnumber">TC Kimlik Numarası</label>
        <input
          type="text"
          id="tcnumber"
          name="tcnumber"
          value={formData.tcnumber}
          onChange={handleInputChange}
          placeholder="TC Kimlik Numarası"
          maxLength="11"
        />
      </div>
      <div className="form-group">
        <label htmlFor="email">Email</label>
        {/* type="email" değil: tarayıcı Türkçe karakterli alan adlarını (güzelbahçe.com)
            geçersiz sayıp kaydı engelliyor ya da punycode'a çevirip e-postayı değiştiriyordu. */}
        <input
          type="text"
          inputMode="email"
          id="email"
          name="email"
          value={formData.email ?? ''}
          onChange={handleInputChange}
          placeholder="Email"
        />
      </div>
      <div className="form-group">
        <label htmlFor="phoneNumber">Telefon Numarası</label>
        <input
          type="tel"
          id="phoneNumber"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={handleInputChange}
          placeholder="05XXXXXXXXX"
          maxLength="11"
        />
        <span className="helper-text">Örnek: 05XXXXXXXXX (11 haneli)</span>
      </div>
      <div className="form-group" style={{display: 'none'}}>
        <label htmlFor="grup">Grup</label>
<select id="grup"
          name="grup"
                  value={formData.grup || ""}
                  onChange={(e) => handleGroupChange(formData.id, e.target.value)}
                 
                >
                  <option value="">Seçiniz</option>
                  {groups.map((group) => (
                    <option key={group.name} value={group.name}>
                      {group.name}
                    </option>
                  ))}
                </select>
      </div>
      <div className="form-group">
        <label htmlFor="role">Rol</label>
        <select
          id="role"
          name="role"
          value={formData.role}
          onChange={handleInputChange}
          disabled={isRolesLoading}
        >
          <option value="">{isRolesLoading ? 'Yükleniyor...' : 'Seçiniz'}</option>
          {roles.map((role) => (
            <option key={role.id} value={String(role.id)}>
              {role.name}
            </option>
          ))}
        </select>
      </div>

      {showIlceMahalle && <IlceMahalleSecimi secim={ilceMahalle} />}

      {error && <div className="error-message">{error}</div>}
      <div className="form-actions">
        <button type="submit" className="submit-button">
            Düzenlemeyi Kaydet
        </button>
      </div>
    </form>
  );

  const renderConfirmation = () => {
    const { ilce, mahlist } = showIlceMahalle ? ilceMahalle.kayitDegerleri() : { ilce: '', mahlist: '' };
    const ilceName = ilce.split(',').join(', ');
    const mahalleNames = mahlist.split(',').join(', ');
    return (
    <div className="confirmation-step">
      <WarningIcon />
      <h3>Kişi bilgilerini güncellemek üzeresiniz. Onaylıyor musunuz?</h3>
      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Ad Soyad</th>
              <th>TC Kimlik No</th>
              <th>Email</th>
              <th>Telefon</th>
              <th>Grup</th>
              <th>Rol</th>
              {showIlceMahalle && (
                <>
                  <th>İlçe</th>
                  <th>Mahalle</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{`${formData.name} ${formData.surname}`}</td>
              <td>{formData.tcnumber}</td>
              <td>{formData.email}</td>
              <td>{formData.phoneNumber}</td>
              <td>{formData.grup}</td>
              <td><RoleBadge role={roles.find((r) => String(r.id) === formData.role)?.name ?? formData.role} /></td>
              {showIlceMahalle && (
                <>
                  <td>{ilceName}</td>
                  <td>{mahalleNames}</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="form-actions">
        <button type="button" onClick={handleBack} className="back-button">
          İptal Et
        </button>
        <button type="button" onClick={handleConfirm} className="confirm-button">
          Sonraki Adım
        </button>
      </div>
    </div>
    );
  };

  const renderComplete = () => (
    <div className="complete-step">
      {isLoading ? (
        <div className="loading-state">
          <Spinner size="large" />
          <h3>Süreciniz tamamlanıyor...</h3>
          <button type="button" onClick={handleClose} className="cancel-button">
            Süreci İptal Et
          </button>
        </div>
      ) : updatedUser ? (
        <div className="success-state">
          <SuccessCircle />
          <h3>Süreciniz tamamlandı. Kişi bilgileri güncellendi.</h3>
          <div className="table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Ad Soyad</th>
                  <th>TC Kimlik No</th>
                  <th>Email</th>
                  <th>Telefon</th>
                  <th>Grup</th>
                  <th>Rol</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{`${updatedUser.name} ${updatedUser.surname}`}</td>
                  <td>{updatedUser.tcnumber}</td>
                  <td>{updatedUser.email}</td>
                  <td>{updatedUser.phoneNumber}</td>
                  <td>{updatedUser.grup}</td>
                  <td><RoleBadge role={roles.find((r) => String(r.id) === String(updatedUser.role))?.name ?? updatedUser.role} /></td>
                </tr>
              </tbody>
            </table>
          </div>
          <button type="button" onClick={handleClose} className="close-button">
            Kapat
          </button>
        </div>
      ) : (
        <div className="error-state">
          <div className="error-icon">✕</div>
          <h3>Hata Oluştu</h3>
          <p>{error}</p>
          <button type="button" onClick={handleBack} className="retry-button">
            Tekrar Dene
          </button>
        </div>
      )}
    </div>
  );

  // Add modal closing behaviors
  useModalClose(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Kişi Bilgilerini Düzenle</h3>
        </div>
        {renderStepIndicator()}
        <div className="modal-body">
          {currentStep === STEPS.FORM && renderForm()}
          {currentStep === STEPS.CONFIRM && renderConfirmation()}
          {currentStep === STEPS.COMPLETE && renderComplete()}
        </div>
      </div>
    </div>
  );
};

export default EditUserModal; 